import { PropertyValueMap, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, query, queryAll, queryAssignedElements, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksBreadcrumbChangeEvent } from '../../events/blocks-breadcrumb-change';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../breadcrumbs-item/breadcrumbs-item';
import { BlocksBreadcrumbsItem } from '../breadcrumbs-item/breadcrumbs-item';
import '../contextual-menu/contextual-menu';
import { BlocksContextualMenu } from '../contextual-menu/contextual-menu';
import '../icon/icons/ellipsis';
import '../list-item/list-item';
import '../list/list';
import '../skeleton/skeleton';
import '../tooltip/tooltip';
import type { CatBreadCrumbItem } from './breadcrumb.model';
import styles from './breadcrumbs.styles';

/**
 * @slot breadcrumb - The breadcrumbs slot when `items` property is not defined.(When using a slot ensure that you provide role="listitem" directly on the <cat-breadcrumbs-item> to maintain proper list semantics for accessibility.)
 * @slot separator- The breadcrumbs separator slot, if `separator` slot is not provided for individual breadcrumb-item.
 * @fires bl-breadcrumb-change - emitted when breadcrumb item is clicked.
 * @csspart base - The component's base wrapper.
 * @csspart list - The component's list.
 * @csspart breadcrumb - The component's breadcrumb slot.
 * @csspart separator - The component's separator slot.
 * @csspart breadcrumbs-item - The component's breadcrumbs-item.
 * @csspart breadcrumbs-item-ellipsis - The component's breadcrumbs-item with ellipsis.
 * @csspart ellipsis - The component's ellipsis wrapper.
 * @csspart tooltip - The component's tooltip for ellipsis.
 * @csspart item-show-more - The component's item for show more.
 * @csspart menu-show-more - The component's `contextual-menu` for show more.
 * @csspart menu-trigger - The component's `contextual-menu` trigger.
 * @csspart menu-after - The component's `contextual-menu` after.
 * @csspart show-more-list - The component's `contextual-menu` show-more list.
 * @csspart show-more-list-item - The component's `contextual-menu` show-more list-item.
 * @csspart list - The component's `contextual-menu` list.
 * @csspart skeleton - The component's skeleton wrapper.
 * @csspart loader - The component's loader wrapper.
 */
export class BlocksBreadcrumbs extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Items for render breadcrumbs
   */
  @property({ type: Array })
  items: CatBreadCrumbItem[] = [];

  /**
   * **Breakpoint below which breadcrumb switches to mobile view.Defaults to 768px.**
   */
  @property({ type: Number })
  mobileBreakPoint = MOBILE_BREAK_POINT;

  /**
   * specifies the number of skeleton loaders to be displayed as breadcrumb-item
   */
  @property({ type: Number })
  loaderCount = 0;

  /**
   * The maximum number of breadcrumbs list to display
   */
  @property({ type: Number })
  maxDisplayedItems = 999;

  /**
   * Optional index where overflow items will be collapsed
   * Default value 2
   */
  @property({ type: Number })
  overflowIndex = 2;

  /**
   * Aria label attribute
   * - Provides an accessible name for the nav element
   */
  @property({ type: String })
  arialabel = 'Breadcrumb';

  /**
   * Aria label attribute
   * - Provides an accessible name contextual menu
   */
  @property({ type: String })
  moreItemsLabel = 'More items';

  /**
   * The maximum number of characters allowed in breadcrumb item's legend. Ellipsis will be added beyond the defined char count.
   * Default allowed legend length is 15
   */
  @property({ type: Number })
  maxCharacterCount = 15;

  @state()
  private _selectedItem: CatBreadCrumbItem | undefined;

  /**
   * Tooltip condition
   * - used to compare with breadcrumbitem index to display tooltip
   */
  @state() private currentHoverItem: number | undefined | null;

  /**
   * Mobile Variant
   */
  @state() private isMobile = false;

  /**
   * used to store index of active item
   */
  @state() private activeIndex: number | undefined;

  @queryAll('cat-breadcrumbs-item')
  private _breadcrumbsItems: NodeListOf<BlocksBreadcrumbsItem> | undefined;

  @query('slot[name="separator"]')
  private _separatorSlot: HTMLSlotElement | undefined;

  @query('cat-contextual-menu')
  private _catContextualMenu: BlocksContextualMenu | undefined;

  @queryAssignedElements({ slot: 'separator', flatten: true })
  private _separatorSlotContent: Array<HTMLElement> | undefined;

  private hascontextualMenuItems = false;

  /**
   *  This variable is used to store item ,which is clicked.
   */
  private currentClickedItem?: CatBreadCrumbItem | null;

  /**
   * used to store current clicked item after updating Items Array
   */
  private currentClickedIndex?: number;

  protected slots = new SlotController(this, 'breadcrumb', 'separator');

  private get hasBreadcrumbSlot(): boolean {
    return this.slots.hasSlotted('breadcrumb');
  }

  /**
   * renderBreadCrumbItems
   * 1 Shallow clone items array
   * 2 Based on overflow index create contextual menu items
   * 3 Since splice mutate array, The remaining item will be in the items array
   * 4 Find index of last rendered item so the divider icon can be hidden
   * 5 Create contextual menu based on contextualMenuItems array and insert show more icon at overflow index position
   * 6 To check the index of active item , to avoid dispatch event and function calls with the help of index.
   */
  private renderBreadCrumbItems() {
    const itemLength = this.items.length - 1;
    const items = [...this.items]; /**1 */
    const maxlabelLength = this.maxCharacterCount;

    const maxDisplayedItems = this.maxDisplayedItems - 1;
    const overflowIndex = this.overflowIndex === 0 ? 1 : this.overflowIndex - 1;
    const contextualMenuItems = items.splice(overflowIndex, itemLength - maxDisplayedItems); /**2 */
    this.hascontextualMenuItems = contextualMenuItems.length !== 0;
    const lastItemIndex = items.length - 1;

    /* 3 */
    let remainingItems = items.map((item, index: number) => {
      const isNotLast = index !== lastItemIndex || (this.hascontextualMenuItems && index === overflowIndex - 1); /* 4 */

      if (this.currentClickedItem && this.currentClickedItem === item) {
        this.currentClickedIndex = index;
      }
      /* 6 */
      const updatedActiveIndex = this.activeIndex ? this.activeIndex - 1 : 0;
      const isActive = this.hascontextualMenuItems
        ? index >= overflowIndex
          ? index === updatedActiveIndex
          : index === this.activeIndex
        : index === this.activeIndex;
      const remainingBreadcrumbItems = this.returnBreadcrumbItems(item, index, maxlabelLength, isNotLast, isActive);
      return remainingBreadcrumbItems;
    });

    /* 5 */
    if (this.hascontextualMenuItems) {
      remainingItems = this.returnItemsAsContextualMenu(overflowIndex, lastItemIndex, remainingItems, contextualMenuItems);
    }

    return remainingItems;
  }

  private renderLoader() {
    const loaderarray: TemplateResult<1>[] = [];
    for (let i = 0; i < this.loaderCount; i++) {
      {
        loaderarray.push(html`
          <cat-breadcrumbs-item
            part="loader"
            exportparts=${this.getExportParts('breadcrumbs-item-loader', 'breadcrumbs-item')}
            ?isMobile=${false}
            ?isLast="${i === this.loaderCount - 1}"
            ?isLoading=${true}
            tabindex="-1"
          >
            <cat-skeleton part="skeleton" exportparts=${this.getExportParts('skeleton')} size="sm" width="60px"></cat-skeleton>
          </cat-breadcrumbs-item>
        `);
      }
    }
    return loaderarray;
  }

  /**
   * return breadcrumb items that needs to be rendered as contextualMenu
   *1. The <cat-breadcrumbs-item> component is assigned role="presentation" for accessibility, and is wrapped inside an <li> with to ensure proper semantic structure and compatibility with screen readers.
   **/
  private returnItemsAsContextualMenu(
    overflowIndex: number,
    lastItemIndex: number,
    remainingItems: TemplateResult<1>[],
    contextualMenuItems: CatBreadCrumbItem[]
  ) {
    const isLast = !(overflowIndex !== lastItemIndex + 1);
    remainingItems.splice(
      overflowIndex,
      0,
      html`
        <li>
          <!-- 1 -->
          <cat-breadcrumbs-item
            part="item-show-more"
            exportparts=${this.getExportParts('breadcrumbs-item-item-show-more', 'breadcrumbs-item')}
            ?isLast=${isLast}
          >
            <cat-contextual-menu
              part="menu-show-more"
              exportparts=${this.getExportParts('contextual-menu')}
              slot="showmore"
              position="bottom-end"
              class="index"
            >
              <div
                part="menu-trigger"
                class="cat-c-breadcrumbs-item-menu-trigger"
                role="button"
                aria-label=${this.moreItemsLabel}
                slot="trigger"
                tabindex="0"
                @keydown=${this.handleKeyDown}
              >
                <cat-icon-ellipsis part="menu-after-icon" exportparts=${this.getExportParts('ellipsis', 'icon')} slot="after"></cat-icon-ellipsis>
              </div>
              <cat-list part="show-more-list" exportparts=${this.getExportParts('list')}>
                ${contextualMenuItems.map((contextualMenuItem, index: number) => {
                  return html`
                    <cat-list-item
                      part="show-more-list-item"
                      exportparts=${this.getExportParts('list-item')}
                      ?isActive=${isLast && contextualMenuItems.length - 1 === index}
                      @bl-select=${() => {
                        return this.onSelect(contextualMenuItem);
                      }}
                      href="${contextualMenuItem.href ?? ''}"
                    >
                      ${contextualMenuItem.label}
                    </cat-list-item>
                  `;
                })}
              </cat-list>
            </cat-contextual-menu>
          </cat-breadcrumbs-item>
        </li>
      `
    );
    return remainingItems;
  }

  /**
   * return breadcrumb items that requires ellipsis and also that doesn`t require ellipsis
   **/
  private returnBreadcrumbItems(item: CatBreadCrumbItem, index: number, maxlabelLength: number, isNotLast: boolean, isActive: boolean) {
    if (item.label && item.label.length > maxlabelLength) {
      let duplicateLabel = item.label;
      const ellipses = '...';
      const ellipsisText = duplicateLabel.slice(0, maxlabelLength);

      duplicateLabel = ellipsisText + ellipses;
      return html`
        <div
          part="ellipsis"
          class="cat-breadcrumbitem-ellipsis"
          @mouseover=${() => {
            this.currentHoverItem = index;
          }}
          @mouseleave=${() => {
            // As tooltip is opened in setTimeout, we need to hide the tooltip on same setTimeout
            setTimeout(() => {
              this.currentHoverItem = null;
            });
          }}
        >
          ${this.currentHoverItem === index
            ? html`
                <cat-tooltip
                  part="tooltip"
                  exportparts="${this.getExportParts('tooltip')}"
                  class="cat-c-breadcrumb__tooltip"
                  ?isActive=${this.currentHoverItem === index}
                  position="top"
                >
                  ${item.label}
                </cat-tooltip>
              `
            : html`${nothing}`}
          <cat-breadcrumbs-item
            part="breadcrumbs-item-ellipsis"
            exportparts=${this.getExportParts('breadcrumbs-item-ellipsis', 'breadcrumbs-item')}
            @click=${() => {
              if (!item?.href) {
                const eventNotPrevented = this.emitPathChangedEvent(item);
                if (eventNotPrevented) {
                  this.checkActive(index);
                }
              }
            }}
            ?isActive=${!isNotLast}
            ?isLast="${!isNotLast}"
            href="${ifDefined(item.href)}"
          >
            ${duplicateLabel}
          </cat-breadcrumbs-item>
        </div>
      `;
    }

    return html`
      <li>
        <cat-breadcrumbs-item
          part="breadcrumbs-item"
          exportparts=${this.getExportParts('breadcrumbs-item')}
          @click=${!isActive
            ? () => {
                if (!item?.href) {
                  const eventNotPrevented = this.emitPathChangedEvent(item);
                  if (eventNotPrevented) {
                    this.checkActive(index);
                    this.currentClickedItem = item;
                  }
                }
              }
            : nothing}
          ?isActive=${!isNotLast}
          ?isLast="${!isNotLast}"
          href="${ifDefined(item.href)}"
          role="presentation"
        >
          ${item.label}
        </cat-breadcrumbs-item>
      </li>
    `;
  }

  /**
   * check if there is a breadcrumbitem as contextual menu and based on it apply and remove active state accordingly.
   */
  private checkActive(index: number) {
    if (!this.hascontextualMenuItems || (this.hascontextualMenuItems && index < this.overflowIndex - 1)) {
      this.removePreviousActiveItem(index);
    } else if (index >= this.overflowIndex - 1) {
      this.removePreviousActiveItem(index + 1);
    }
  }

  /**
   * iterate through breadcrumb items and apply active state to currently selected breadcrumbitem and
   * remove active state from previously selected item.
   */
  private removePreviousActiveItem(index: number) {
    if (this._breadcrumbsItems) {
      const breadcrumbArray = Array.from(this._breadcrumbsItems);
      const isActive = (item: BlocksBreadcrumbsItem) => item.isActive === true;
      const prevItem = breadcrumbArray.find(isActive);
      if (prevItem) {
        prevItem.isActive = false;
        prevItem.removeAttribute('tabindex');
        prevItem.removeAttribute('aria-hidden');
      }
      this._breadcrumbsItems[index].isActive = true;
      this.activeIndex = index;
    }
  }

  /**
   * Sets mobile variant to active if screen size is below mobileBreakPoint property.
   */
  private setMobile = () => {
    const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    if (width < this.mobileBreakPoint) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
  };

  /**
   * Function to check active index.
   */
  private checkActiveItem(): void {
    const items = [...this.items];
    const itemLength = items.length - 1;
    const maxDisplayedItems = this.maxDisplayedItems - 1;
    const overflowIndex = this.overflowIndex === 0 ? 1 : this.overflowIndex - 1;
    const contextualMenuItems = items.splice(overflowIndex, itemLength - maxDisplayedItems); /**2 */
    this.hascontextualMenuItems = contextualMenuItems.length !== 0;
    this.activeIndex = this.hascontextualMenuItems ? itemLength - this.overflowIndex + 1 : itemLength;
  }

  /**
   * Connected callback lifecycle
   * 1.To check the index of active item , to avoid dispatch event and function calls with the help of index.
   */
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.setMobile);
    /* 1 */
    this.checkActiveItem();
  }

  /**
   * Disconnected callback lifecycle
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.setMobile);
  }
  /**
   * @fire pathChanged
   * Close contextual menu when we select contextual menu list
   */
  private onSelect(item: CatBreadCrumbItem): void {
    const eventNotPrevented = this.emitPathChangedEvent(item);
    if (eventNotPrevented) {
      this._catContextualMenu?.close();
    }
  }

  /**
   * Close / open contextual menu when we select contextual menu item through keyboard
   */
  private handleKeyDown(evt: KeyboardEvent): void {
    if (this.isMobile && (evt.key === KEYS.ENTER || evt.key === KEYS.SPACE)) {
      this.navigateToByMobile();
      return;
    }
    if (evt.key === KEYS.ESCAPE || evt.key === KEYS.TAB) {
      if (this._catContextualMenu?.isActive) {
        this._catContextualMenu.close();
      }
    } else if (evt.key === KEYS.DOWN_ARROW || evt.key === KEYS.ENTER || evt.key === KEYS.SPACE) {
      evt.preventDefault();
      this._catContextualMenu?.open();
    }
  }

  private navigateToByMobile(): void {
    if (this.items.length !== 1) {
      const lastItem = this.items[this.items.length - 1];
      const eventNotPrevented = this.emitPathChangedEvent(lastItem);
      if (eventNotPrevented) {
        const oldValue = [...this.items];
        this.items.splice(this.items.length - 1, 1);
        this.requestUpdate('items', oldValue);
      }
    }
  }

  /**
   *
   * @param item selected breadcrumb item
   * @fire pathChanged event
   * @returns true if the event was not prevented, false if it was prevented
   */
  private emitPathChangedEvent(item: CatBreadCrumbItem): boolean {
    const event = this.dispatch({
      eventName: 'bl-breadcrumb-change',
      detailObj: { item },
      optionsObj: { cancelable: true }
    }) as BlocksBreadcrumbChangeEvent;

    return !event.defaultPrevented;
  }

  /**
   * handleSlotChange
   * 1) Append separators to each item if they don't already have one
   */
  private handleSlotChange(): void {
    let items: BlocksBreadcrumbsItem[];

    /*
     * If called when the application passes input through items property of <cat-breadcrumbs> component
     * else called when the inputs are passed through <cat-breadcrumbs-item>
     */
    if (this._breadcrumbsItems && this._breadcrumbsItems.length > 0) {
      items = Array.from(this._breadcrumbsItems);
    } else {
      items = Array.from(this.querySelectorAll('cat-breadcrumbs-item'));
    }

    items.forEach((item) => {
      /* 1 */
      const separator = item.querySelector('[slot="separator"]');
      if (!item.hasAttribute('islast')) {
        if (separator === null) {
          item.append(this.getSeparator());
        } else if (separator.hasAttribute('data-custom-icon')) {
          separator.replaceWith(this.getSeparator());
        }
      }
    });
  }

  /**
   * getSeparator
   * 1 Generates a clone of the separator element to use for each breadcrumb item
   * 2 If custom separator provided set data-custom-icon attribute,
   * so that during run time if dir changes we can toggle icon based on dir attribute
   */
  private getSeparator() {
    const separator = this._separatorSlot?.assignedElements({ flatten: true })[0] as HTMLElement; /* 1 */
    const clone = separator.cloneNode(true) as HTMLElement;
    clone.setAttribute('data-custom-icon', ''); /* 2 */

    return clone;
  }

  /**
   * 1. To trigger checkActive after updation of items array
   */
  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('mobileBreakPoint')) {
      this.setMobile();
    }

    if (this.isMobile) {
      this._selectedItem = this.items[this.items.length - 1];
    }
    /* 1 */
    if (_changedProperties.has('items')) {
      if (this.currentClickedIndex && this.currentClickedIndex >= 0) {
        this.checkActive(this.currentClickedIndex);
        this.currentClickedItem = null;
        this.currentClickedIndex = -1;
      }
      if (this._separatorSlotContent?.[0]) {
        this.handleSlotChange();
      }
    }
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-breadcrumbs', {
      'cat-c-breadcrumbs__item-loader': this.loaderCount,
      'cat-c-breadcrumbs--has-breadcrumbs': this.hasBreadcrumbSlot,
      'cat-c-breadcrumbs--has-separator': this.slots.hasSlotted('separator')
    });

    let innerHtml;
    if (this.loaderCount) {
      innerHtml = this.renderLoader();
    } else {
      innerHtml = this.isMobile
        ? html`
            <li>
              <cat-breadcrumbs-item
                part="breadcrumbs-item-mobile"
                exportparts=${this.getExportParts('breadcrumbs-item-mobile', 'breadcrumbs-item')}
                @click=${this.navigateToByMobile}
                ?isMobile=${true}
                ?isLast=${true}
                @keydown=${this.handleKeyDown}
                href="${ifDefined(this._selectedItem?.href)}"
              >
                ${this._selectedItem?.label}
              </cat-breadcrumbs-item>
            </li>
          `
        : this.renderBreadCrumbItems();
    }

    return html`
      <nav part="base" class="${componentClassNames}" aria-label=${this.arialabel}>
        <ul part="list" role="list" class="cat-c-breadcrumbs__list" inert=${ifDefined(this.loaderCount > 0 ? true : undefined)}>
          <slot part="breadcrumb" name="breadcrumb"></slot>
          ${!this.hasBreadcrumbSlot ? innerHtml : nothing}
        </ul>
      </nav>
      <slot part="separator" @slotchange=${this.handleSlotChange} hidden name="separator" inert=${true}></slot>
    `;
  }
}

if (customElements.get('cat-breadcrumbs') === undefined) {
  customElements.define('cat-breadcrumbs', BlocksBreadcrumbs);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-breadcrumbs': BlocksBreadcrumbs;
  }
}
